import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { type DAMNodeType, type TreeRecursiveChildrenNode } from '@/types/tree';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { searchNodes } from '@/services/tree.service';
import { QueryKey } from '@/lib/queryKeys';

export const MIN_TREE_SEARCH_LENGTH = 1;
const DEBOUNCE_DELAY_MS = 250;

export const TREE_SEARCH_SCOPE = {
  DEFAULT: 'default',
  INGESTION: 'ingestion',
} as const;

type TreeSearchScope = (typeof TREE_SEARCH_SCOPE)[keyof typeof TREE_SEARCH_SCOPE];

export const useTreeSearch = ({
  type,
  scope = TREE_SEARCH_SCOPE.DEFAULT,
}: {
  type?: DAMNodeType;
  scope?: TreeSearchScope;
} = {}) => {
  const searchTerm = useTreeStore((state) => state.searchState[scope]?.searchTerm ?? '');
  const committedTerm = useTreeStore((state) => state.searchState[scope]?.committedTerm ?? '');
  const setSearchTermStore = useTreeStore((state) => state.setSearchTerm);
  const setCommittedTermStore = useTreeStore((state) => state.setCommittedTerm);
  const resetSearchStore = useTreeStore((state) => state.reset);

  const setSearchTerm = useCallback(
    (value: string) => {
      setSearchTermStore(value, scope);
    },
    [scope, setSearchTermStore],
  );

  const setCommittedTerm = useCallback(
    (value: string) => {
      setCommittedTermStore(value, scope);
    },
    [scope, setCommittedTermStore],
  );

  const resetSearch = useCallback(() => {
    resetSearchStore(scope);
  }, [resetSearchStore, scope]);

  const queryClient = useQueryClient();
  const searchNodesFn = useAuthenticatedQueryFn(searchNodes);

  const debouncedCommit = useMemo(
    () =>
      debounce((value: string) => {
        setCommittedTerm(value);
      }, DEBOUNCE_DELAY_MS),
    [setCommittedTerm],
  );

  useEffect(() => {
    const trimmedValue = searchTerm.trim();

    if (trimmedValue.length === 0) {
      debouncedCommit.cancel();
      setCommittedTerm('');
      return;
    }

    if (trimmedValue.length < MIN_TREE_SEARCH_LENGTH) {
      debouncedCommit.cancel();
      setCommittedTerm('');
      return;
    }

    debouncedCommit(trimmedValue);

    return () => {
      debouncedCommit.cancel();
    };
  }, [searchTerm, debouncedCommit]);

  useEffect(
    () => () => {
      debouncedCommit.cancel();
    },
    [debouncedCommit],
  );

  const { data, isFetching, isError, error } = useQuery<Array<TreeRecursiveChildrenNode>>({
    queryKey: [QueryKey.TreeSearch, { query: committedTerm, type }],
    queryFn: () => searchNodesFn({ search: committedTerm, ...(type ? { type } : {}) }),
    enabled: committedTerm.length >= MIN_TREE_SEARCH_LENGTH,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const clearSearch = useCallback(() => {
    debouncedCommit.cancel();
    resetSearch();
    queryClient.removeQueries({ queryKey: [QueryKey.TreeSearch], exact: false });
  }, [queryClient, debouncedCommit, resetSearch]);

  const isActive = useMemo(() => committedTerm.length >= MIN_TREE_SEARCH_LENGTH, [committedTerm]);

  return {
    searchTerm,
    setSearchTerm,
    committedTerm,
    results: isActive ? (data ?? []) : [],
    isFetching,
    isError,
    error,
    isActive,
    clearSearch,
  };
};
